/* eslint-disable @next/next/no-img-element */
"use client";

import {
  Activity,
  AlertTriangle,
  ArrowRight,
  BarChart3,
  Bot,
  Boxes,
  Brain,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  CircleHelp,
  Clock3,
  Cloud,
  Database,
  FileText,
  GitBranch,
  LayoutGrid,
  Network,
  Package,
  PanelLeft,
  Play,
  Plug,
  Plus,
  RefreshCw,
  Route,
  Search,
  Sparkles,
  Table2,
  Users,
  Workflow,
  Zap,
  type LucideIcon,
} from "lucide-react";
import type { ReactNode } from "react";

import { ASSET } from "./data";

export type ProductSurface =
  | "overview"
  | "sources"
  | "objects"
  | "agents"
  | "monitor"
  | "studio"
  | "records";

type NavItem = {
  label: string;
  surface?: ProductSurface;
  icon: LucideIcon;
};

const PRODUCT_NAV: Array<{ label: string; items: NavItem[] }> = [
  {
    label: "Platform",
    items: [
      { label: "Overview", surface: "overview", icon: LayoutGrid },
      { label: "Sources", surface: "sources", icon: Plug },
      { label: "Data Catalog", icon: Database },
      { label: "Business Objects", surface: "objects", icon: Brain },
    ],
  },
  {
    label: "AI Space",
    items: [
      { label: "Custom Agents", surface: "agents", icon: Bot },
      { label: "Skills", icon: FileText },
    ],
  },
  {
    label: "Action Layer",
    items: [
      { label: "Playbook Monitor", surface: "monitor", icon: Activity },
      { label: "Playbook Studio", surface: "studio", icon: Workflow },
      { label: "Orchestrator", icon: Route },
      { label: "Records", surface: "records", icon: Table2 },
    ],
  },
];

function ProductSidebar({ active }: { active: ProductSurface }) {
  return (
    <aside className="pv-sidebar" aria-hidden="true">
      <div className="pv-workspace">
        <span className="pv-workspace-mark">W</span>
        <span>
          <strong>Demo workspace</strong>
          <small>Wayak</small>
        </span>
        <ChevronRight size={12} />
      </div>
      <div className="pv-chat-link">
        <Sparkles size={13} /> Chat
      </div>
      <div className="pv-nav-groups">
        {PRODUCT_NAV.map((group) => (
          <div className="pv-nav-group" key={group.label}>
            <small>{group.label}</small>
            {group.items.map((item) => {
              const Icon = item.icon;
              const selected = item.surface === active;
              return (
                <div
                  className={"pv-nav-item" + (selected ? " is-active" : "")}
                  key={item.label}
                >
                  <Icon size={12} />
                  <span>{item.label}</span>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </aside>
  );
}

export function ProductFrame({
  active,
  children,
  className = "",
}: {
  active: ProductSurface;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={`pv-frame ${className}`}>
      <ProductSidebar active={active} />
      <div className="pv-main">
        <div className="pv-topbar">
          <div className="pv-topbar-brand">
            <PanelLeft size={13} />
            <span />
            <img src={ASSET + "/logo_main_black.png"} alt="Wayak" />
          </div>
          <div className="pv-demo-badge">Sanitized demo workspace</div>
        </div>
        {children}
      </div>
    </div>
  );
}

export function PageHeader({
  icon: Icon,
  title,
  description,
  action,
}: {
  icon: LucideIcon;
  title: string;
  description: string;
  action?: string;
}) {
  return (
    <div className="pv-page-header">
      <div className="pv-page-title">
        <Icon size={18} />
        <span>
          <strong>{title}</strong>
          <small>{description}</small>
        </span>
      </div>
      <div className="pv-header-actions">
        <CircleHelp size={13} />
        {action ? (
          <span className="pv-solid-button">
            <Plus size={12} /> {action}
          </span>
        ) : null}
      </div>
    </div>
  );
}

export function AskWayak({ placeholder }: { placeholder: string }) {
  return (
    <div className="pv-ask">
      <div className="pv-ask-context">
        <Bot size={10} /> Wayak has this page in context
      </div>
      <div className="pv-ask-input">
        <Sparkles size={13} />
        <span>{placeholder}</span>
        <span className="pv-ask-send">↑</span>
      </div>
    </div>
  );
}

export function KpiCard({
  label,
  value,
  icon: Icon,
  tone = "neutral",
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  tone?: "neutral" | "sky" | "green" | "rose";
}) {
  return (
    <div className={`pv-kpi pv-tone-${tone}`}>
      <div>
        <span>{label}</span>
        <Icon size={12} />
      </div>
      <strong>{value}</strong>
    </div>
  );
}

export function StatusPill({
  children,
  tone = "green",
}: {
  children: ReactNode;
  tone?: "green" | "sky" | "amber" | "rose" | "slate";
}) {
  return <span className={`pv-pill pv-pill-${tone}`}>{children}</span>;
}

export function ProductMonitorPreview() {
  return (
    <ProductFrame active="monitor" className="pv-hero-frame">
      <div className="pv-page pv-monitor-page">
        <PageHeader
          icon={Activity}
          title="Playbook Monitor"
          description="Operational view for runs, schedules, and generated outputs."
        />
        <div className="pv-product-tabs">
          <span className="is-active">Monitor</span>
          <span>Studio</span>
        </div>
        <AskWayak placeholder="Ask Wayak about recent runs, failures, or outputs..." />
        <div className="pv-kpi-grid">
          <KpiCard label="Running now" value="2" icon={RefreshCw} tone="sky" />
          <KpiCard label="Scheduled today" value="5" icon={CalendarDays} />
          <KpiCard
            label="Succeeded today"
            value="18"
            icon={CheckCircle2}
            tone="green"
          />
          <KpiCard
            label="Failed today"
            value="1"
            icon={AlertTriangle}
            tone="rose"
          />
          <KpiCard label="Outputs this week" value="12" icon={Package} />
        </div>
        <div className="pv-monitor-grid">
          <section className="pv-panel pv-running-panel">
            <div className="pv-panel-heading">
              <span>
                <Clock3 size={12} /> Running now
              </span>
              <StatusPill tone="sky">2 live</StatusPill>
            </div>
            <div className="pv-run-line">
              <div className="pv-run-title">
                <span className="pv-icon-box pv-icon-violet">
                  <Workflow size={12} />
                </span>
                <span>
                  <strong>Exception review</strong>
                  <small>4 of 6 steps complete</small>
                </span>
              </div>
              <span>67%</span>
            </div>
            <div className="pv-progress">
              <i style={{ width: "67%" }} />
            </div>
            <div className="pv-run-meta">
              <span>Currently: Prepare review draft</span>
              <span>1m 42s</span>
            </div>
          </section>
          <section className="pv-panel pv-activity-panel">
            <div className="pv-panel-heading">
              <span>
                <Activity size={12} /> Recent activity
              </span>
              <span className="pv-text-link">View all</span>
            </div>
            <div className="pv-activity-row">
              <CheckCircle2 size={13} />
              <span>
                <strong>Policy sync</strong>
                <small>6 records · completed</small>
              </span>
              <time>2m</time>
            </div>
            <div className="pv-activity-row">
              <AlertTriangle size={13} />
              <span>
                <strong>Exception review</strong>
                <small>1 record needs attention</small>
              </span>
              <time>5m</time>
            </div>
          </section>
        </div>
      </div>
    </ProductFrame>
  );
}

function SourceRow({
  initials,
  tone,
  name,
  kind,
  destination,
  status,
  badge,
}: {
  initials: string;
  tone: string;
  name: string;
  kind: string;
  destination: string;
  status: string;
  badge?: string;
}) {
  return (
    <div className="pv-source-row">
      <span className={`pv-source-mark ${tone}`}>{initials}</span>
      <span className="pv-source-copy">
        <span>
          <strong>{name}</strong>
          <small>{kind}</small>
        </span>
        <code>{destination}</code>
        <span className="pv-source-state">
          <i /> {status}
          {badge ? <StatusPill tone="green">{badge}</StatusPill> : null}
          <em>tested today</em>
        </span>
      </span>
      <span className="pv-outline-button">
        <Sparkles size={10} /> Ask
      </span>
      <ChevronRight size={12} />
    </div>
  );
}

export function SourcesPreview() {
  return (
    <ProductFrame active="sources" className="pv-stage-frame">
      <div className="pv-page pv-source-page">
        <PageHeader
          icon={Plug}
          title="Sources"
          description="Live connections that let Wayak reach your data."
          action="New source"
        />
        <div className="pv-snapshot">
          <p>
            Wayak can reach <strong>3 sources</strong>. Two databases are
            queryable live, and one API syncs into the catalog.
          </p>
          <div>
            <StatusPill tone="sky">2 databases</StatusPill>
            <StatusPill tone="green">1 API</StatusPill>
            <StatusPill tone="slate">All healthy</StatusPill>
          </div>
        </div>
        <AskWayak placeholder="Ask Wayak what is connected or what to add next..." />
        <div className="pv-section-label">
          <span>
            <Database size={12} /> Databases <em>2 connections</em>
          </span>
          <span>Connect a database</span>
        </div>
        <div className="pv-source-grid">
          <SourceRow
            initials="PG"
            tone="sky"
            name="Operations warehouse"
            kind="POSTGRES"
            destination="warehouse/operations"
            status="Connected"
            badge="Live access on"
          />
          <SourceRow
            initials="BQ"
            tone="indigo"
            name="Finance analytics"
            kind="BIGQUERY"
            destination="finance/reporting"
            status="Connected"
            badge="Live access on"
          />
        </div>
        <div className="pv-section-label">
          <span>
            <Cloud size={12} /> API connections <em>1 connection</em>
          </span>
          <span>Connect an API</span>
        </div>
        <SourceRow
          initials="API"
          tone="cyan"
          name="Support events"
          kind="API"
          destination="support.ticket_events"
          status="Synced"
          badge="Hourly"
        />
      </div>
    </ProductFrame>
  );
}

function ObjectCard({
  icon: Icon,
  name,
  description,
  source,
  tone,
}: {
  icon: LucideIcon;
  name: string;
  description: string;
  source: string;
  tone: string;
}) {
  return (
    <div className={`pv-object-card ${tone}`}>
      <span>
        <Icon size={12} />
        <strong>{name}</strong>
      </span>
      <p>{description}</p>
      <small>from {source}</small>
      <span className="pv-object-ask">
        <Sparkles size={9} /> Ask
      </span>
    </div>
  );
}

export function ModelPreview() {
  return (
    <ProductFrame active="objects" className="pv-stage-frame">
      <div className="pv-page pv-model-page">
        <PageHeader
          icon={Brain}
          title="Business Objects"
          description="The shared vocabulary Wayak uses to answer questions about your data."
          action="Model something"
        />
        <div className="pv-snapshot pv-model-snapshot">
          <p>
            Wayak recognizes the core concepts in your operation and how they
            connect across source tables.
          </p>
          <div>
            <StatusPill tone="sky">6 objects</StatusPill>
            <StatusPill tone="amber">5 relationships</StatusPill>
            <StatusPill tone="slate">4/5 tables covered</StatusPill>
          </div>
        </div>
        <AskWayak placeholder="Ask Wayak how these objects connect..." />
        <div className="pv-track">
          <div className="pv-track-heading">
            <span>
              <Users size={12} /> Entities <em>3</em>
            </span>
            <small>Add an entity</small>
          </div>
          <div className="pv-object-grid">
            <ObjectCard
              icon={Users}
              name="Customer"
              description="The person or business receiving service."
              source="crm.accounts"
              tone="sky"
            />
            <ObjectCard
              icon={FileText}
              name="Claim"
              description="A request moving through review and resolution."
              source="ops.claims"
              tone="sky"
            />
            <ObjectCard
              icon={Boxes}
              name="Policy"
              description="Rules and coverage attached to an account."
              source="policy.active"
              tone="sky"
            />
          </div>
        </div>
        <div className="pv-neighborhood">
          <div className="pv-neighborhood-heading">
            <strong>How it connects</strong>
            <small>Where the data comes from and what depends on it.</small>
          </div>
          <div className="pv-neighborhood-grid">
            <div>
              <small>FROM SOURCE DATA</small>
              <span>
                <Database size={11} /> ops.claims
              </span>
            </div>
            <ArrowRight size={12} />
            <div className="is-focus">
              <small>THIS OBJECT</small>
              <span>
                <FileText size={11} /> Claim
              </span>
            </div>
            <ArrowRight size={12} />
            <div>
              <small>USED DOWNSTREAM</small>
              <span>
                <BarChart3 size={11} /> Review dashboard
              </span>
            </div>
          </div>
        </div>
      </div>
    </ProductFrame>
  );
}

function FlowIcons({ variant = "full" }: { variant?: "full" | "short" }) {
  const steps =
    variant === "short"
      ? [Database, Brain, FileText]
      : [Database, Sparkles, GitBranch, FileText];
  return (
    <span className="pv-flow-icons">
      {steps.map((Icon, index) => (
        <span key={index}>
          <i>
            <Icon size={9} />
          </i>
          {index < steps.length - 1 ? <ArrowRight size={8} /> : null}
        </span>
      ))}
    </span>
  );
}

function PlaybookRow({
  title,
  description,
  variant,
}: {
  title: string;
  description: string;
  variant?: "full" | "short";
}) {
  return (
    <div className="pv-playbook-row">
      <span className="pv-playbook-mark">
        <Workflow size={13} />
      </span>
      <span className="pv-playbook-copy">
        <span>
          <strong>{title}</strong>
          <StatusPill tone="green">Active</StatusPill>
          <small>ran 2m ago</small>
        </span>
        <p>{description}</p>
        <FlowIcons variant={variant} />
      </span>
      <span className="pv-playbook-actions">
        <span className="pv-outline-button">
          <Sparkles size={10} /> Ask
        </span>
        <span className="pv-outline-button">
          <Play size={10} /> Run now
        </span>
      </span>
    </div>
  );
}

export function OperatePreview() {
  return (
    <ProductFrame active="studio" className="pv-stage-frame">
      <div className="pv-page pv-studio-page">
        <PageHeader
          icon={Workflow}
          title="Playbook Studio"
          description="Find, open, and manage the automations your organization has built."
          action="New playbook"
        />
        <div className="pv-product-tabs">
          <span>Monitor</span>
          <span className="is-active">Studio</span>
        </div>
        <div className="pv-studio-kpis">
          <KpiCard label="Total" value="2" icon={Workflow} />
          <KpiCard label="Active" value="2" icon={CheckCircle2} tone="green" />
          <KpiCard label="Draft" value="0" icon={FileText} />
          <KpiCard label="Failing" value="0" icon={AlertTriangle} />
        </div>
        <AskWayak placeholder="Ask Wayak to draft, revise, or inspect a playbook..." />
        <div className="pv-filter-row">
          <span>
            <Search size={11} /> Search playbooks...
          </span>
          <div>
            <span>All time</span>
            <span>Any capability</span>
            <span>All statuses</span>
          </div>
        </div>
        <div className="pv-playbook-list">
          <PlaybookRow
            title="Weekly operations brief"
            description="Pulls operating views, evaluates exceptions, and produces a review-ready report."
            variant="full"
          />
          <PlaybookRow
            title="Exception review"
            description="Collects a record, applies policy rules, and routes uncertain outcomes to review."
            variant="short"
          />
        </div>
      </div>
    </ProductFrame>
  );
}

export function ProductPreviewStyles() {
  return (
    <style>{`
      @keyframes pvLive { 0%,58%,100% { opacity: .42; transform: scale(.78); } 68% { opacity: 1; transform: scale(1); } 78% { opacity: .62; transform: scale(.9); } }
      @keyframes pvProgressSheen { 0%,42% { opacity: 0; transform: translateX(-130%); } 50% { opacity: .75; } 72% { opacity: .75; transform: translateX(360%); } 80%,100% { opacity: 0; transform: translateX(360%); } }
      @keyframes pvRelationTrace { 0%,48%,100% { color: #a1a1aa; opacity: .48; transform: translateX(0); } 58%,70% { color: #18181b; opacity: 1; transform: translateX(2px); } }
      @keyframes pvFlowStep { 0%,48%,76%,100% { border-color: #d4d4d8; background: var(--pv-soft); color: #27272a; transform: scale(1); } 56%,68% { border-color: #18181b; background: #18181b; color: #fff; transform: scale(1.08); } }

      .pv-frame {
        --pv-ink: #09090b;
        --pv-muted: #71717a;
        --pv-border: #e4e4e7;
        --pv-soft: #f4f4f5;
        position: relative;
        display: grid;
        grid-template-columns: 182px minmax(0, 1fr);
        overflow: hidden;
        border: 1px solid var(--pv-border);
        border-radius: 20px;
        background: #fff;
        color: var(--pv-ink);
        box-shadow: 0 30px 90px rgba(0,0,0,.32);
        text-align: left;
      }
      .pv-hero-frame { width: min(100%, 1180px); min-height: 510px; margin: 72px auto 0; }
      .pv-stage-frame { width: 100%; min-height: 510px; margin-top: 30px; grid-template-columns: 145px minmax(0,1fr); border-radius: 16px; box-shadow: none; }
      .pv-sidebar { min-width: 0; padding: 11px 10px 16px; overflow: hidden; border-right: 1px solid var(--pv-border); background: #fafafa; }
      .pv-workspace { display: grid; grid-template-columns: 28px 1fr 12px; align-items: center; gap: 8px; padding: 3px 2px 12px; }
      .pv-workspace-mark { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 7px; background: #18181b; color: #fff; font-size: 10px; font-weight: 700; }
      .pv-workspace strong, .pv-workspace small { display: block; white-space: nowrap; }
      .pv-workspace strong { font-size: 9px; }
      .pv-workspace small { margin-top: 2px; color: var(--pv-muted); font-size: 8px; }
      .pv-chat-link { display: flex; align-items: center; gap: 7px; margin: 8px 2px 13px; padding: 7px 8px; border-radius: 6px; font-size: 9px; }
      .pv-nav-groups { display: grid; gap: 14px; }
      .pv-nav-group > small { display: block; margin: 0 7px 5px; color: #9999a1; font-size: 7px; letter-spacing: .05em; }
      .pv-nav-group { display: grid; gap: 2px; }
      .pv-nav-item { display: flex; align-items: center; gap: 7px; min-width: 0; padding: 5px 7px; border-left: 2px solid transparent; border-radius: 4px; color: #5d5d65; font-size: 8px; white-space: nowrap; }
      .pv-nav-item.is-active { border-left-color: #18181b; background: #eeeeef; color: #18181b; font-weight: 600; }
      .pv-main { min-width: 0; background: #fff; }
      .pv-topbar { height: 45px; display: flex; align-items: center; justify-content: space-between; padding: 0 13px; border-bottom: 1px solid var(--pv-border); }
      .pv-topbar-brand { display: flex; align-items: center; gap: 10px; }
      .pv-topbar-brand > span { width: 1px; height: 14px; background: var(--pv-border); }
      .pv-topbar-brand img { width: auto; height: 15px; }
      .pv-demo-badge { padding: 5px 8px; border: 1px solid #d9d9dd; border-radius: 999px; color: #71717a; font-size: 7px; letter-spacing: .08em; text-transform: uppercase; }
      .pv-page { padding: 24px 28px 28px; }
      .pv-stage-frame .pv-page { padding: 20px 22px 22px; }
      .pv-page-header { display: flex; align-items: flex-start; justify-content: space-between; gap: 18px; }
      .pv-page-title { display: flex; align-items: flex-start; gap: 10px; }
      .pv-page-title > svg { margin-top: 2px; }
      .pv-page-title strong, .pv-page-title small { display: block; }
      .pv-page-title strong { font-size: 19px; line-height: 1.1; letter-spacing: -.025em; }
      .pv-page-title small { max-width: 520px; margin-top: 5px; color: var(--pv-muted); font-size: 9px; line-height: 1.4; }
      .pv-header-actions { display: flex; align-items: center; gap: 10px; color: var(--pv-muted); }
      .pv-solid-button, .pv-outline-button { display: inline-flex; align-items: center; justify-content: center; gap: 5px; border-radius: 5px; font-weight: 600; white-space: nowrap; }
      .pv-solid-button { padding: 8px 11px; background: #18181b; color: #fff; font-size: 8px; }
      .pv-outline-button { padding: 6px 8px; border: 1px solid #d9d9de; background: #fff; color: #27272a; font-size: 7px; }
      .pv-product-tabs { display: inline-flex; gap: 2px; margin-top: 14px; padding: 2px; border: 1px solid var(--pv-border); border-radius: 8px; background: #f7f7f8; }
      .pv-product-tabs span { padding: 5px 10px; border-radius: 6px; color: var(--pv-muted); font-size: 8px; }
      .pv-product-tabs .is-active { background: #fff; color: #18181b; box-shadow: 0 1px 3px rgba(0,0,0,.08); }
      .pv-ask { margin-top: 13px; padding: 7px; border: 1px solid var(--pv-border); border-radius: 10px; background: #fff; box-shadow: 0 1px 2px rgba(0,0,0,.03); }
      .pv-ask-context { display: flex; align-items: center; gap: 5px; padding: 0 3px 5px; color: #8a8a92; font-size: 7px; }
      .pv-ask-input { display: grid; grid-template-columns: 14px 1fr 24px; align-items: center; gap: 6px; padding: 6px 7px; border: 1px solid #e6e6e9; border-radius: 7px; color: #9b9ba2; font-size: 9px; }
      .pv-ask-send { width: 24px; height: 24px; display: grid; place-items: center; border-radius: 5px; background: #929299; color: #fff; font-size: 10px; }
      .pv-kpi-grid { display: grid; grid-template-columns: repeat(5, 1fr); gap: 8px; margin-top: 13px; }
      .pv-kpi, .pv-panel { border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; box-shadow: none; }
      .pv-kpi { min-width: 0; padding: 10px; }
      .pv-kpi > div { display: flex; align-items: center; justify-content: space-between; gap: 5px; color: var(--pv-muted); font-size: 7px; }
      .pv-kpi strong { display: block; margin-top: 5px; font-size: 18px; line-height: 1; font-variant-numeric: tabular-nums; }
      .pv-tone-sky, .pv-tone-green, .pv-tone-rose { border-color: var(--pv-border); background: #fff; color: var(--pv-ink); }
      .pv-tone-sky strong, .pv-tone-sky svg { color: #0369a1; }
      .pv-tone-green strong, .pv-tone-green svg { color: #166534; }
      .pv-tone-rose strong, .pv-tone-rose svg { color: #9f1239; }
      .pv-monitor-grid { display: grid; grid-template-columns: 1.06fr .94fr; gap: 10px; margin-top: 10px; }
      .pv-panel { padding: 12px; }
      .pv-panel-heading { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding-bottom: 9px; border-bottom: 1px solid #ededee; }
      .pv-panel-heading > span:first-child { display: flex; align-items: center; gap: 6px; font-size: 9px; font-weight: 700; }
      .pv-text-link { color: #777780; font-size: 7px; }
      .pv-pill { display: inline-flex; align-items: center; gap: 4px; padding: 3px 6px; border: 1px solid transparent; border-radius: 999px; font-size: 7px; font-style: normal; font-weight: 600; line-height: 1; white-space: nowrap; }
      .pv-pill-green { border-color: #bbf7d0; background: #f0fdf4; color: #166534; }
      .pv-pill-sky { border-color: #bae6fd; background: #f0f9ff; color: #075985; }
      .pv-pill-amber { border-color: #fde68a; background: #fffbeb; color: #92400e; }
      .pv-pill-rose { border-color: #fecdd3; background: #fff1f2; color: #9f1239; }
      .pv-pill-slate { border-color: #e2e8f0; background: #f8fafc; color: #475569; }
      .pv-run-line { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 12px; font-size: 8px; }
      .pv-run-title { display: flex; align-items: center; gap: 8px; min-width: 0; }
      .pv-run-title strong, .pv-run-title small { display: block; }
      .pv-run-title strong { font-size: 9px; }
      .pv-run-title small { margin-top: 2px; color: var(--pv-muted); font-size: 7px; }
      .pv-icon-box { width: 28px; height: 28px; display: grid; place-items: center; border-radius: 7px; }
      .pv-icon-violet { background: var(--pv-soft); color: #18181b; }
      .pv-progress { height: 5px; margin-top: 10px; overflow: hidden; border-radius: 99px; background: #ececf0; }
      .pv-progress i { position: relative; display: block; height: 100%; overflow: hidden; border-radius: inherit; background: #18181b; }
      .pv-progress i::after { content: ""; position: absolute; inset: 0 auto 0 0; width: 34%; background: linear-gradient(90deg,transparent,rgba(255,255,255,.7),transparent); animation: pvProgressSheen 6.2s cubic-bezier(.45,0,.2,1) infinite; }
      .pv-running-panel .pv-pill-sky::before { content: ""; width: 4px; height: 4px; border-radius: 50%; background: currentColor; animation: pvLive 2.8s ease-in-out infinite; }
      .pv-run-meta { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 8px; color: var(--pv-muted); font-size: 7px; }
      .pv-activity-row { display: grid; grid-template-columns: 15px 1fr auto; align-items: center; gap: 7px; padding: 8px 0; border-bottom: 1px solid #efeff1; }
      .pv-activity-row:last-child { border-bottom: 0; }
      .pv-activity-row:first-of-type svg { color: #16a34a; }
      .pv-activity-row:nth-of-type(3) svg { color: #d97706; }
      .pv-activity-row strong, .pv-activity-row small { display: block; }
      .pv-activity-row strong { font-size: 8px; }
      .pv-activity-row small, .pv-activity-row time { margin-top: 2px; color: var(--pv-muted); font-size: 7px; }
      .pv-snapshot { margin-top: 15px; padding: 12px; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; box-shadow: none; }
      .pv-snapshot p { margin: 0; font-size: 9px; line-height: 1.5; }
      .pv-snapshot > div { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 8px; }
      .pv-section-label, .pv-track-heading { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin: 15px 0 7px; }
      .pv-section-label > span:first-child, .pv-track-heading > span { display: flex; align-items: center; gap: 5px; font-size: 9px; font-weight: 700; }
      .pv-section-label em, .pv-track-heading em { color: var(--pv-muted); font-size: 7px; font-style: normal; font-weight: 400; }
      .pv-section-label > span:last-child, .pv-track-heading > small { color: var(--pv-muted); font-size: 7px; }
      .pv-source-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 7px; }
      .pv-source-row { display: grid; grid-template-columns: 34px minmax(0,1fr) auto 12px; align-items: center; gap: 8px; min-width: 0; padding: 9px; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; box-shadow: none; }
      .pv-source-mark { width: 34px; height: 34px; display: grid; place-items: center; border: 1px solid; border-radius: 8px; font-size: 8px; font-weight: 700; }
      .pv-source-mark.sky, .pv-source-mark.indigo, .pv-source-mark.cyan { border-color: #d4d4d8; background: var(--pv-soft); color: #27272a; }
      .pv-source-copy { min-width: 0; }
      .pv-source-copy > span:first-child { display: flex; align-items: baseline; gap: 5px; }
      .pv-source-copy strong { overflow: hidden; font-size: 8px; text-overflow: ellipsis; white-space: nowrap; }
      .pv-source-copy small { color: var(--pv-muted); font-size: 7px; letter-spacing: .08em; }
      .pv-source-copy code { display: block; overflow: hidden; margin-top: 2px; color: var(--pv-muted); font-family: monospace; font-size: 7px; text-overflow: ellipsis; white-space: nowrap; }
      .pv-source-state { display: flex; align-items: center; gap: 5px; margin-top: 4px; color: #166534; font-size: 7px; font-style: normal; }
      .pv-source-state > i { width: 4px; height: 4px; border-radius: 50%; background: #22c55e; animation: pvLive 4.8s ease-in-out infinite; }
      .pv-source-state em { color: var(--pv-muted); font-size: 7px; font-style: normal; }
      .pv-model-snapshot p { max-width: 530px; }
      .pv-object-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 7px; }
      .pv-object-card { position: relative; min-height: 104px; padding: 11px 10px 9px 13px; overflow: hidden; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; box-shadow: none; }
      .pv-object-card::before { content: ""; position: absolute; left: 0; top: 12px; bottom: 12px; width: 3px; border-radius: 99px; background: #27272a; }
      .pv-object-card > span:first-child { display: flex; align-items: center; gap: 6px; color: #27272a; }
      .pv-object-card strong { color: #18181b; font-size: 8px; }
      .pv-object-card p { margin: 7px 0 0; color: var(--pv-muted); font-size: 7px; line-height: 1.4; }
      .pv-object-card > small { position: absolute; left: 13px; bottom: 9px; color: #96969e; font-size: 7px; }
      .pv-object-ask { position: absolute; right: 7px; bottom: 6px; display: flex; align-items: center; gap: 3px; padding: 3px 5px; border: 1px solid #dedee2; border-radius: 5px; color: #44444a; font-size: 7px; }
      .pv-neighborhood { margin-top: 14px; padding: 12px; border: 1px solid var(--pv-border); border-radius: 12px; }
      .pv-neighborhood-heading strong, .pv-neighborhood-heading small { display: block; }
      .pv-neighborhood-heading strong { font-size: 9px; }
      .pv-neighborhood-heading small { margin-top: 2px; color: var(--pv-muted); font-size: 7px; }
      .pv-neighborhood-grid { display: grid; grid-template-columns: 1fr 14px 1fr 14px 1fr; align-items: center; gap: 5px; margin-top: 10px; }
      .pv-neighborhood-grid > div { min-width: 0; padding: 8px; border: 1px solid var(--pv-border); border-radius: 7px; background: #fff; }
      .pv-neighborhood-grid > svg { animation: pvRelationTrace 6.4s cubic-bezier(.45,0,.2,1) infinite; }
      .pv-neighborhood-grid > svg:nth-of-type(2) { animation-delay: .7s; }
      .pv-neighborhood-grid > div.is-focus { background: #f4f4f5; border-color: #c9c9ce; }
      .pv-neighborhood-grid small { display: block; color: var(--pv-muted); font-size: 7px; letter-spacing: .08em; }
      .pv-neighborhood-grid span { display: flex; align-items: center; gap: 5px; overflow: hidden; margin-top: 4px; font-size: 7px; font-weight: 600; text-overflow: ellipsis; white-space: nowrap; }
      .pv-studio-kpis { display: grid; grid-template-columns: repeat(4, 1fr); gap: 7px; margin-top: 12px; }
      .pv-filter-row { display: flex; align-items: center; justify-content: space-between; gap: 10px; margin-top: 12px; }
      .pv-filter-row > span { width: 46%; display: flex; align-items: center; gap: 6px; padding: 7px 9px; border: 1px solid var(--pv-border); border-radius: 6px; color: #9a9aa2; font-size: 7px; }
      .pv-filter-row > div { display: flex; gap: 5px; }
      .pv-filter-row > div span { padding: 7px 9px; border: 1px solid var(--pv-border); border-radius: 6px; color: #66666e; font-size: 7px; }
      .pv-playbook-list { display: grid; gap: 7px; margin-top: 12px; }
      .pv-playbook-row { display: grid; grid-template-columns: 34px minmax(0,1fr) auto; align-items: center; gap: 9px; padding: 10px; border: 1px solid var(--pv-border); border-radius: 12px; background: #fff; box-shadow: none; }
      .pv-playbook-mark { width: 34px; height: 34px; display: grid; place-items: center; border-radius: 8px; background: var(--pv-soft); color: #27272a; }
      .pv-playbook-copy { min-width: 0; }
      .pv-playbook-copy > span:first-child { display: flex; align-items: center; gap: 5px; }
      .pv-playbook-copy strong { font-size: 8px; }
      .pv-playbook-copy > span:first-child > small { color: var(--pv-muted); font-size: 7px; }
      .pv-playbook-copy p { overflow: hidden; margin: 4px 0 0; color: var(--pv-muted); font-size: 7px; text-overflow: ellipsis; white-space: nowrap; }
      .pv-flow-icons { display: flex; align-items: center; gap: 2px; margin-top: 5px; }
      .pv-flow-icons > span { display: flex; align-items: center; gap: 2px; color: #a1a1aa; }
      .pv-flow-icons i { width: 15px; height: 15px; display: grid; place-items: center; border: 1px solid #d4d4d8; border-radius: 50%; background: var(--pv-soft); color: #27272a; animation: pvFlowStep 7.2s cubic-bezier(.16,1,.3,1) infinite; }
      .pv-flow-icons > span:nth-child(2) i { animation-delay: .55s; }
      .pv-flow-icons > span:nth-child(3) i { animation-delay: 1.1s; }
      .pv-flow-icons > span:nth-child(4) i { animation-delay: 1.65s; }
      .pv-playbook-actions { display: flex; gap: 5px; }

      @media (max-width: 1080px) {
        .pv-hero-frame { grid-template-columns: 155px minmax(0,1fr); }
        .pv-stage-frame { grid-template-columns: 160px minmax(0,1fr); }
        .pv-source-grid { grid-template-columns: 1fr; }
      }

      @media (max-width: 820px) {
        .pv-frame, .pv-hero-frame, .pv-stage-frame { grid-template-columns: 1fr; }
        .pv-sidebar { display: none; }
        .pv-hero-frame { min-height: auto; margin-top: 54px; }
        .pv-page, .pv-stage-frame .pv-page { padding: 18px; }
        .pv-demo-badge { display: none; }
        .pv-kpi-grid { grid-template-columns: repeat(2,1fr); }
        .pv-kpi-grid .pv-kpi:last-child { grid-column: 1 / -1; }
        .pv-monitor-grid { grid-template-columns: 1fr; }
        .pv-source-grid, .pv-object-grid { grid-template-columns: 1fr; }
        .pv-object-card { min-height: 100px; }
        .pv-neighborhood-grid { grid-template-columns: 1fr; }
        .pv-neighborhood-grid > svg { transform: rotate(90deg); justify-self: center; }
        .pv-studio-kpis { grid-template-columns: repeat(2,1fr); }
        .pv-filter-row { align-items: stretch; flex-direction: column; }
        .pv-filter-row > span { width: auto; }
        .pv-filter-row > div { display: none; }
        .pv-playbook-row { grid-template-columns: 30px minmax(0,1fr); }
        .pv-playbook-actions { grid-column: 2; }
      }

      @media (max-width: 520px) {
        .pv-page-title strong { font-size: 16px; }
        .pv-page-title small { font-size: 8px; }
        .pv-header-actions > svg { display: none; }
        .pv-solid-button { padding: 7px; font-size: 0; }
        .pv-solid-button svg { margin: 0; }
        .pv-ask-context { display: none; }
        .pv-source-row { grid-template-columns: 32px minmax(0,1fr) 10px; }
        .pv-source-row .pv-outline-button { display: none; }
        .pv-studio-kpis { grid-template-columns: repeat(2,1fr); }
        .pv-playbook-actions { display: none; }
      }

      @media (prefers-reduced-motion: reduce) {
        .pv-progress i::after, .pv-running-panel .pv-pill-sky::before, .pv-source-state > i, .pv-neighborhood-grid > svg, .pv-flow-icons i { animation: none !important; transform: none !important; }
      }
    `}</style>
  );
}
